import { useEffect, useRef } from 'react'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { useOrganizationStore } from '@/store/organization.store'
import { useQueryClient } from '@tanstack/react-query'

import { SocketEvent } from '@scrambl/shared'

import { useRealtime } from '../core/useRealtime'
import { subscribeInventoryInvalidation } from '../inventory-query-invalidation'

export const inventoryEvents = [
  SocketEvent.INVENTORY_ITEM_CREATED,
  SocketEvent.INVENTORY_ITEM_UPDATED,
  SocketEvent.INVENTORY_ITEM_DELETED,
  SocketEvent.INVENTORY_QUANTITY_CHANGED,
  SocketEvent.INVENTORY_MOVEMENT_RECORDED,
  SocketEvent.INVENTORY_ADJUSTMENT_CREATED,
  SocketEvent.INVENTORY_ADJUSTMENT_APPROVED,
  SocketEvent.INVENTORY_ADJUSTMENT_REJECTED,
  SocketEvent.INVENTORY_CATEGORY_CREATED,
  SocketEvent.INVENTORY_CATEGORY_UPDATED,
  SocketEvent.INVENTORY_CATEGORY_DELETED,
  SocketEvent.INVENTORY_LOCATION_CREATED,
  SocketEvent.INVENTORY_LOCATION_UPDATED,
  SocketEvent.INVENTORY_LOCATION_DELETED,
]

/** Events are notices only. Always replace data through the current reader API. */
export function useInventoryRealtime({
  enabled = true,
  joinOrganizationRoom = true,
}: {
  itemId?: string
  enabled?: boolean
  joinOrganizationRoom?: boolean
} = {}) {
  const orgId = useOrganizationStore(state => state.currentOrganization?.id)
  const scope = useWorkspaceSession()
  const client = useQueryClient()
  const { socket, isReady, joinRoom, leaveRoom } = useRealtime({
    suppressLogging: true,
  })
  const subscription = useRef<ReturnType<
    typeof subscribeInventoryInvalidation
  > | null>(null)
  useEffect(() => {
    if (!enabled || !scope.enabled) return
    const entry = subscribeInventoryInvalidation(client, scope)
    subscription.current = entry
    return () => {
      entry.dispose()
      subscription.current = null
    }
  }, [client, enabled, scope])
  const connection = useRef({ seen: false, ready: false })
  useEffect(() => {
    if (isReady && connection.current.seen && !connection.current.ready)
      subscription.current?.refreshAll()
    connection.current = {
      seen: connection.current.seen || isReady,
      ready: isReady,
    }
  }, [isReady])
  useEffect(() => {
    if (!enabled || !scope.enabled || !socket || !isReady || !orgId) return
    const notice = (data: Record<string, unknown>) => {
      if (scope.signal.aborted || data?.organizationId !== orgId) return
      // Ignore legacy writer payloads as well: their values never enter the cache.
      const eventId = data._eventId ?? data.eventId
      subscription.current?.notify(
        typeof eventId === 'string' ? eventId : undefined,
      )
    }
    if (joinOrganizationRoom) joinRoom('organization', orgId)
    for (const event of inventoryEvents) socket.on(event, notice)
    return () => {
      for (const event of inventoryEvents) socket.off(event, notice)
      if (joinOrganizationRoom) leaveRoom('organization', orgId)
    }
  }, [
    enabled,
    scope,
    socket,
    isReady,
    orgId,
    joinOrganizationRoom,
    joinRoom,
    leaveRoom,
  ])
}
